import api from './api';

class CompanyService {
  constructor() {
    this.baseUrl = '/companies';
  }

  // Get company profile
  async getCompanyProfile() {
    try {
      const response = await api.get(`${this.baseUrl}/profile`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Update company profile
  async updateCompanyProfile(companyData) {
    try {
      const response = await api.put(`${this.baseUrl}/profile`, companyData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get company statistics
  async getCompanyStatistics() {
    try {
      const response = await api.get(`${this.baseUrl}/statistics`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get dashboard data
  async getDashboardData() {
    try {
      const response = await api.get(`${this.baseUrl}/dashboard`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get company users
  async getCompanyUsers(filters = {}) {
    try {
      const response = await api.get(`${this.baseUrl}/users`, { params: filters });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Update subscription
  async updateSubscription(subscriptionData) {
    try {
      const response = await api.put(`${this.baseUrl}/subscription`, subscriptionData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get compliance score
  async getComplianceScore() {
    try {
      const response = await api.get(`${this.baseUrl}/compliance-score`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Lookup company by DOT number
  async getCompanyByDotNumber(dotNumber) {
    try {
      const response = await api.get(`${this.baseUrl}/by-dot/${dotNumber}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // 🚫 REMOVED: getCompanyByMcNumber method

  // Get activity summary
  async getActivitySummary() {
    try {
      const response = await api.get(`${this.baseUrl}/activity-summary`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Health check
  async healthCheck() {
    try {
      const response = await api.get(`${this.baseUrl}/health-check`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Validate company data
  validateCompanyData(companyData) {
    const errors = [];

    // Required fields validation
    if (!companyData.name || companyData.name.trim().length === 0) {
      errors.push('Company name is required');
    }

    if (!companyData.dotNumber || companyData.dotNumber.trim().length === 0) {
      errors.push('DOT number is required');
    }

    // DOT number format validation (should be numeric, 1-8 digits)
    if (companyData.dotNumber && !/^\d{1,8}$/.test(companyData.dotNumber)) {
      errors.push('DOT number must be 1-8 digits');
    }

    // MC number format validation (optional, but if provided should be valid)
    if (companyData.mcNumber && !/^MC\d{6}$/.test(companyData.mcNumber)) {
      errors.push('MC number must be in format MC123456');
    }

    // Email validation
    if (companyData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyData.email)) {
      errors.push('Invalid email format');
    }

    // Phone validation (basic format check)
    if (companyData.phone && !/^\+?[\d\s\-\(\)]+$/.test(companyData.phone)) {
      errors.push('Invalid phone number format');
    }

    // Address validation
    if (companyData.address) {
      if (!companyData.address.street) {
        errors.push('Street address is required');
      }
      if (!companyData.address.city) {
        errors.push('City is required');
      }
      if (!companyData.address.state) {
        errors.push('State is required');
      }
      if (!companyData.address.zipCode) {
        errors.push('ZIP code is required');
      }
      // ZIP code format validation (US format)
      if (companyData.address.zipCode && !/^\d{5}(-\d{4})?$/.test(companyData.address.zipCode)) {
        errors.push('ZIP code must be in format 12345 or 12345-6789');
      }
    }

    // Fleet size validation
    if (companyData.fleetSize !== undefined && companyData.fleetSize < 0) {
      errors.push('Fleet size cannot be negative');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Format company data for API
  formatCompanyData(companyData) {
    return {
      name: companyData.name?.trim(),
      dotNumber: companyData.dotNumber?.trim(),
      mcNumber: companyData.mcNumber?.trim() || null,
      address: {
        street: companyData.address?.street?.trim(),
        city: companyData.address?.city?.trim(),
        state: companyData.address?.state?.trim(),
        zipCode: companyData.address?.zipCode?.trim(),
        country: companyData.address?.country?.trim() || 'USA'
      },
      phone: companyData.phone?.trim(),
      email: companyData.email?.trim(),
      website: companyData.website?.trim() || null,
      fleetSize: companyData.fleetSize || 0,
      subscriptionPlan: companyData.subscriptionPlan || 'basic'
    };
  }

  // Get subscription plans
  getSubscriptionPlans() {
    return [
      {
        id: 'basic',
        name: 'Basic',
        description: 'Essential compliance management',
        features: [
          'Up to 5 vehicles',
          'Basic document management',
          'Email support'
        ],
        price: 29.99,
        maxVehicles: 5
      },
      {
        id: 'pro',
        name: 'Professional',
        description: 'Advanced fleet management',
        features: [
          'Up to 25 vehicles',
          'Advanced reporting',
          'Document automation',
          'Priority support'
        ],
        price: 79.99,
        maxVehicles: 25
      },
      {
        id: 'enterprise',
        name: 'Enterprise',
        description: 'Unlimited fleet management',
        features: [
          'Unlimited vehicles',
          'Custom integrations',
          'Dedicated support',
          'Advanced analytics'
        ],
        price: 199.99,
        maxVehicles: null
      }
    ];
  }

  // Get US states list
  getUsStates() {
    return [
      { code: 'AL', name: 'Alabama' },
      { code: 'AK', name: 'Alaska' },
      { code: 'AZ', name: 'Arizona' },
      { code: 'AR', name: 'Arkansas' },
      { code: 'CA', name: 'California' },
      { code: 'CO', name: 'Colorado' },
      { code: 'CT', name: 'Connecticut' },
      { code: 'DE', name: 'Delaware' },
      { code: 'FL', name: 'Florida' },
      { code: 'GA', name: 'Georgia' },
      { code: 'HI', name: 'Hawaii' },
      { code: 'ID', name: 'Idaho' },
      { code: 'IL', name: 'Illinois' },
      { code: 'IN', name: 'Indiana' },
      { code: 'IA', name: 'Iowa' },
      { code: 'KS', name: 'Kansas' },
      { code: 'KY', name: 'Kentucky' },
      { code: 'LA', name: 'Louisiana' },
      { code: 'ME', name: 'Maine' },
      { code: 'MD', name: 'Maryland' },
      { code: 'MA', name: 'Massachusetts' },
      { code: 'MI', name: 'Michigan' },
      { code: 'MN', name: 'Minnesota' },
      { code: 'MS', name: 'Mississippi' },
      { code: 'MO', name: 'Missouri' },
      { code: 'MT', name: 'Montana' },
      { code: 'NE', name: 'Nebraska' },
      { code: 'NV', name: 'Nevada' },
      { code: 'NH', name: 'New Hampshire' },
      { code: 'NJ', name: 'New Jersey' },
      { code: 'NM', name: 'New Mexico' },
      { code: 'NY', name: 'New York' },
      { code: 'NC', name: 'North Carolina' },
      { code: 'ND', name: 'North Dakota' },
      { code: 'OH', name: 'Ohio' },
      { code: 'OK', name: 'Oklahoma' },
      { code: 'OR', name: 'Oregon' },
      { code: 'PA', name: 'Pennsylvania' },
      { code: 'RI', name: 'Rhode Island' },
      { code: 'SC', name: 'South Carolina' },
      { code: 'SD', name: 'South Dakota' },
      { code: 'TN', name: 'Tennessee' },
      { code: 'TX', name: 'Texas' },
      { code: 'UT', name: 'Utah' },
      { code: 'VT', name: 'Vermont' },
      { code: 'VA', name: 'Virginia' },
      { code: 'WA', name: 'Washington' },
      { code: 'WV', name: 'West Virginia' },
      { code: 'WI', name: 'Wisconsin' },
      { code: 'WY', name: 'Wyoming' }
    ];
  }

  // Helper methods for company operations
  generateSessionId() {
    return `company_onboarding_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  formatDotNumber(dotNumber) {
    // Remove any non-numeric characters
    return dotNumber.replace(/\D/g, '');
  }

  formatMcNumber(mcNumber) {
    // Ensure MC prefix and proper format
    const numericPart = mcNumber.replace(/\D/g, '');
    return numericPart ? `MC${numericPart.padStart(6, '0')}` : '';
  }

  formatPhoneNumber(phone) {
    // Basic phone number formatting (US format)
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone; // Return original if can't format
  }

  // Check if DOT number exists
  async checkDotNumberExists(dotNumber) {
    try {
      await this.getCompanyByDotNumber(dotNumber);
      return true;
    } catch (error) {
      if (error.status === 404) {
        return false;
      }
      throw error;
    }
  }

  // 🚫 REMOVED: checkMcNumberExists method

  // Onboarding methods
  async createCompany(companyData) {
    try {
      const formattedData = this.formatCompanyData(companyData);
      const response = await api.post(this.baseUrl, formattedData);
      return response.data?.id || response.data?.companyId || response.companyId || response.id;
    } catch (error) {
      throw error;
    }
  }

  // Create company with onboarding progress tracking
  // Update company onboarding progress
  async updateOnboardingProgress(companyId, progressData) {
    try {
      const response = await api.patch(`${this.baseUrl}/${companyId}/onboarding-progress`, {
        onboardingProgress: {
          ...progressData,
          lastUpdated: new Date().toISOString()
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Complete company onboarding
  // Get company onboarding status
  async getOnboardingStatus(companyId) {
    try {
      const response = await api.get(`${this.baseUrl}/${companyId}/onboarding-status`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

}

const companyService = new CompanyService();
export default companyService;
